// src/frontend/src/pages/StarterKits.tsx - Fixed version with correct API integration
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { starterKits } from '../utils/starterKits';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';

interface BackendKitProgress {
  _id: string;
  starterKit: {
    _id: string;
    slug: string;
    name: string;
  };
  status: 'not_started' | 'in_progress' | 'completed';
  currentScenarioIndex: number;
  scenarioProgress: Array<{
    scenario: string;
    status: 'not_started' | 'in_progress' | 'completed';
    score?: number;
    completedAt?: string;
    orderIndex: number;
  }>;
  averageScore?: number;
  completedAt?: string;
}

interface ProcessedProgress {
  [kitSlug: string]: {
    completedScenarios: number;
    totalScenarios: number;
    progressPercentage: number;
    status: string;
    averageScore?: number;
  };
}

const StarterKits: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userProgress, setUserProgress] = useState<ProcessedProgress>({});
  const [isLoadingProgress, setIsLoadingProgress] = useState(true);
  const [userPlan, setUserPlan] = useState<any>(null);

  useEffect(() => {
    fetchUserProgress();
    fetchUserPlan();
  }, []);

  const fetchUserProgress = async () => {
    try {
      setIsLoadingProgress(true);
      
      // Use the correct API endpoint
      const response = await api.get('/starter-kits/my-progress');
      const backendProgress: BackendKitProgress[] = response.data.data || [];
      
      // Process the backend data into the format the frontend expects
      const processedProgress: ProcessedProgress = {};
      
      backendProgress.forEach((kitProgress) => {
        const kitSlug = kitProgress.starterKit.slug;
        const completedScenarios = kitProgress.scenarioProgress.filter(
          sp => sp.status === 'completed'
        ).length;
        const totalScenarios = kitProgress.scenarioProgress.length;
        const progressPercentage = totalScenarios > 0 ? 
          Math.round((completedScenarios / totalScenarios) * 100) : 0;
        
        processedProgress[kitSlug] = {
          completedScenarios,
          totalScenarios,
          progressPercentage,
          status: kitProgress.status,
          averageScore: kitProgress.averageScore
        };
      });
      
      setUserProgress(processedProgress);
      console.log('Processed progress:', processedProgress); // Debug log
    } catch (err) {
      console.error('Failed to fetch user progress:', err);
      
      // Initialize with empty progress for all kits
      const emptyProgress: ProcessedProgress = {};
      starterKits.forEach(kit => {
        emptyProgress[kit.id] = {
          completedScenarios: 0,
          totalScenarios: kit.scenarios.length,
          progressPercentage: 0,
          status: 'not_started'
        };
      });
      setUserProgress(emptyProgress);
    } finally {
      setIsLoadingProgress(false);
    }
  };

  const fetchUserPlan = async () => {
    try {
      const response = await api.get('/users/plan');
      setUserPlan(response.data.data);
    } catch (err) {
      console.error('Failed to fetch user plan:', err);
    }
  };

  const getColorClasses = (color: string) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-600',
        hover: 'hover:bg-blue-100',
        button: 'bg-blue-600 hover:bg-blue-700'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-600',
        hover: 'hover:bg-green-100',
        button: 'bg-green-600 hover:bg-green-700'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-600',
        hover: 'hover:bg-purple-100',
        button: 'bg-purple-600 hover:bg-purple-700'
      }
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const getKitProgress = (kitId: string) => {
    const progress = userProgress[kitId];
    if (!progress) {
      // Return default values if no progress found
      const kit = starterKits.find(k => k.id === kitId);
      return { 
        percentage: 0, 
        completed: 0, 
        total: kit?.scenarios.length || 0,
        status: 'not_started'
      };
    }
    
    return {
      percentage: progress.progressPercentage,
      completed: progress.completedScenarios,
      total: progress.totalScenarios,
      status: progress.status
    };
  };

  const getKitStatus = (kitId: string) => {
    const progress = getKitProgress(kitId);
    if (progress.percentage === 0) return 'Not Started';
    if (progress.percentage === 100) return 'Completed';
    return 'In Progress';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Not Started':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const handleKitStart = (kitSlug: string) => {
    // Navigate to the specific kit detail page
    navigate(`/learning-tracks/${kitSlug}`);
  };

  const getEstimatedTime = (kitId: string) => {
    const timeMap = {
      'new-pm-essentials': '2-3 hours',
      'senior-pm-challenges': '3-4 hours',
      'pmo-leadership-track': '4-5 hours'
    };
    return timeMap[kitId as keyof typeof timeMap] || '2-3 hours';
  };

  const getTargetAudience = (kitId: string) => {
    const audienceMap = {
      'new-pm-essentials': 'New project managers',
      'senior-pm-challenges': 'Experienced PMs ready for senior roles',
      'pmo-leadership-track': 'PMO directors'
    };
    return audienceMap[kitId as keyof typeof audienceMap] || 'Project managers';
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Learning Tracks</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Structured coaching paths designed for your PM career stage. Each track contains carefully 
            curated scenarios that build upon each other to develop specific skills and competencies.
          </p>
        </div>

        {/* Trial Status Banner */}
        {userPlan?.isTrialActive && (
          <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-blue-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-blue-800 font-medium">
                    Free Trial Active - {userPlan.trialDaysLeft} days remaining
                  </p>
                  <p className="text-blue-600 text-sm">
                    Access all learning tracks with your {userPlan.creditsTotal - userPlan.creditsUsed} remaining sessions
                  </p>
                </div>
              </div>
              <Link
                to="/pricing"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Upgrade to Continue
              </Link>
            </div>
          </div>
        )}

        {/* Debug Info - Remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              Debug: Loading Progress: {isLoadingProgress ? 'Yes' : 'No'} | 
              Progress Keys: {Object.keys(userProgress).join(', ')} |
              Total Kits: {starterKits.length}
            </p>
          </div>
        )}

        {/* Learning Tracks Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {starterKits.map((kit) => {
            const colors = getColorClasses(kit.color);
            const progress = getKitProgress(kit.id);
            const status = getKitStatus(kit.id);
            const estimatedTime = getEstimatedTime(kit.id);
            const targetAudience = getTargetAudience(kit.id);

            return (
              <div
                key={kit.id}
                className={`relative bg-white border-2 ${colors.border} rounded-xl p-6 hover:shadow-lg transition-all duration-200 group`}
              >
                {/* Status Badge */}
                <div className="absolute top-4 right-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                    {status}
                  </span>
                </div>

                {/* Kit Icon and Title */}
                <div className="mb-6">
                  <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                    <span className="text-3xl">{kit.icon}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{kit.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{kit.description}</p>
                </div>

                {/* Kit Metadata */}
                <div className="mb-6 space-y-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>Target: {targetAudience}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{estimatedTime}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>{progress.total} scenarios</span>
                  </div>
                </div>

                {/* Progress Bar */}
                {!isLoadingProgress && (
                  <div className="mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Progress</span>
                      <span className={`font-medium ${colors.text}`}>
                        {progress.completed}/{progress.total} scenarios
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${colors.button.split(' ')[0]}`}
                        style={{ width: `${progress.percentage}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{progress.percentage}% complete</span>
                      {progress.percentage > 0 && (
                        <span>{status === 'Completed' ? 'Well done!' : 'Keep going!'}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Loading State */}
                {isLoadingProgress && (
                  <div className="mb-6">
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-2 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                )}

                {/* Learning Outcomes Preview */}
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">You'll Learn:</h4>
                  <ul className="space-y-2">
                    {kit.benefits.slice(0, 3).map((benefit, index) => (
                      <li key={index} className="flex items-start text-sm text-gray-700">
                        <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{benefit}</span>
                      </li>
                    ))}
                    {kit.benefits.length > 3 && (
                      <li className="text-sm text-gray-500 ml-6">
                        +{kit.benefits.length - 3} more outcomes
                      </li>
                    )}
                  </ul>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => handleKitStart(kit.id)}
                  className={`w-full text-center py-3 px-4 rounded-lg font-medium text-white transition-colors ${colors.button}`}
                >
                  {status === 'Not Started' ? 'Start Track' : 
                   status === 'Completed' ? 'Review Track' : 'Continue Track'}
                </button>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA Section */}
        <div className="text-center bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Not Sure Where to Start?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Take our quick assessment to find the learning track that best matches your 
            experience level and career goals. Get personalized recommendations in under 2 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/coaching-sessions"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Browse All Scenarios
            </Link>
            <Link
              to="/coaching-history"
              className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
              </svg>
              View Your Progress
            </Link>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Need help choosing the right track?{' '}
            <a href="mailto:support@pmcoach.app" className="text-blue-600 hover:text-blue-700 underline">
              Contact our support team
            </a>{' '}
            for personalized recommendations.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default StarterKits;